import React, { useState } from "react";
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Button, 
  Typography, 
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import headerLogo from "./../assets/images/headerLogo.svg";

const CustomAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#0C0B23", // Dark background
  boxShadow: "none",
  padding: "10px 0px",
  height: "auto", // Changed from fixed height to auto for better responsiveness
  [theme.breakpoints.down("sm")]: {
    padding: "5px 0px",
  }
}));

const GradientButton = styled(Button)({
  background: "linear-gradient(90deg, #1E90FF, #A020F0)", // Blue to Purple
  color: "#fff",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: "700",
  borderRadius: "20px",
  padding: "5px 15px",
  marginLeft: "20px",
  visibility: "hidden", // Keeps the space but hides the button
  "&:hover": {
    opacity: 0.9,
    background: "linear-gradient(90deg, #1E90FF, #A020F0)",
  },
});

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuItems = [
    { label: "Features", url: "https://www.eximius.ai/#Features" },
    { label: "Why Eximius AI", url: "https://www.eximius.ai/#whyEximius" },
    { label: "Contact", url: "https://www.eximius.ai/#Contact" },
  ];

  const handleLogoClick = () => {
    window.location.href = "https://www.eximius.ai/";
  };

  const handleMenuClick = (url: string) => {
    window.location.href = url; // Navigate to the given URL
    if (mobileOpen) {
      setMobileOpen(false); // Close drawer when menu item is clicked
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250, bgcolor: "#0C0B23", height: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton 
          onClick={handleDrawerToggle} 
          sx={{ color: '#FFFFFF' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            key={item.label} 
            onClick={() => handleMenuClick(item.url)}
            sx={{ 
              py: 2,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemText 
              primary={item.label} 
              primaryTypographyProps={{ 
                sx: { color: '#FFFFFF', textAlign: 'left', fontSize: '18px', paddingLeft: '20px' } 
              }} 
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <CustomAppBar position="fixed">
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              padding: { xs: "10px 0", md: "0px 0px 0px 0px" },
              display: "flex",
              justifyContent: "space-between", // Default for larger screens
              [theme.breakpoints.down("md")]: {
                justifyContent: "flex-start", // left align for mobile/tablet
              },
            }}
          >
            

            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ color: "#FFFFFF", marginRight:"4px" }}
              >
                <MenuIcon sx={{ fontSize: 45 }} /> 
              </IconButton>
            )}

            {/* Logo */}
            <Box
              sx={{
                paddingTop: { xs: "5px", md: "10px" },
                display: "flex",
                alignItems: "center",
                paddingLeft: "0px",
                cursor: "pointer",
                marginLeft: { xs: "0px", md: "-5px" },
              }}
              onClick={handleLogoClick}
            >
              <img 
                src={headerLogo} 
                alt="logo" 
                style={{ 
                  height: isMobile ? "40px" : "49px" 
                }} 
              />
            </Box>

             {/* Desktop Navigation Menu */}
             {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  paddingTop: "5px",
                  alignItems: "left",
                  paddingRight: "0px",
                }}
                className="nav-menu"
              >
                {menuItems.map((item) => (
                  <Typography
                    key={item.label}
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "16px",
                      marginLeft: "28px",
                      marginRight: "5px",
                      fontWeight: 400,
                      cursor: "pointer",
                      "&:hover": {
                        color: "#1E90FF"
                      }
                    }}
                    onClick={() => handleMenuClick(item.url)}
                  >
                    {item.label}
                  </Typography>
                ))}

                {/* Gradient Button */}
                {/* <GradientButton variant="contained">Try Me</GradientButton> */}
              </Box>
            )}

          </Toolbar>
        </Container>
      </CustomAppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, 
        }}
      >
        {drawer}
      </Drawer>

      {/* Add padding to account for fixed header */}
      <Toolbar sx={{ height: { xs: "70px", md: "91px" } }} />
    </>
  );
};

export default Header;